/**
 * <tc-locale-bar>: shipping country and language as two quiet dropdowns in the
 * announcement bar (snippets/tc-locale-bar.liquid).
 *
 * The element wires itself in connectedCallback because there are two of it:
 * the bar's own, and the copy main.js clones into the phone menu, whose ids
 * have all been renamed. Nothing is found by id; everything is found from
 * `this`.
 *
 * Choosing a row puts its value in the form's hidden field and submits — one
 * choice, one reload, as the stock selector did.
 *
 * The countries we price are rendered in the page. The rest come from the
 * country-selector section on the first search: that is the list the stock
 * selector fetched, in the page's language, and parsing it here keeps the
 * two in step without the page carrying 228 rows.
 */
(() => {
  if (customElements.get('tc-locale-bar')) return;

  const fold = (s) => s.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase();

  function parseCountries(html) {
    const tmpl = document.createElement('template');
    tmpl.innerHTML = html;
    return [...tmpl.content.querySelectorAll('[data-value]')].map((li) => {
      const name = (li.querySelector('.country-option__name') || li).textContent.trim();
      // "(GBP £)" → "£ GBP", to read like the trigger in the bar.
      const raw = (li.querySelector('.country-option__currency')?.textContent || '')
        .replace(/\u00a0/g, ' ').trim().replace(/^\(|\)$/g, '');
      const [iso, ...symbol] = raw.split(' ');
      return {
        code: li.dataset.value,
        name,
        currency: iso ? `${symbol.join(' ')} ${iso}`.trim() : '',
        key: fold(name),
      };
    });
  }

  class TcLocaleBar extends HTMLElement {
    connectedCallback() {
      this.outside = this.outside || ((e) => { if (this.open && !this.contains(e.target)) this.close(); });
      document.addEventListener('click', this.outside);
      if (this.wired) return;
      this.wired = true;

      this.form = this.querySelector('form');
      this.search = this.querySelector('[data-search]');
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('keydown', (e) => this.onKey(e));
      this.search?.addEventListener('input', () => this.filter());
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.outside);
    }

    trigger(name) { return this.querySelector(`[data-open="${name}"]`); }
    field(name) { return this.form?.querySelector(`input[name="${name}"]`); }
    pop(name) { return this.querySelector(`[data-pop="${name}"]`); }

    onClick(e) {
      const trigger = e.target.closest('[data-open]');
      if (trigger) {
        const name = trigger.dataset.open;
        if (this.open === name) this.close();
        else this.show(name);
        return;
      }
      const country = e.target.closest('[data-country]');
      if (country) { this.choose('country_code', country.dataset.country); return; }
      const locale = e.target.closest('[data-locale]');
      if (locale) this.choose('locale_code', locale.dataset.locale);
    }

    show(name) {
      this.close();
      const pop = this.pop(name);
      if (!pop) return;
      pop.hidden = false;
      this.open = name;
      this.trigger(name).setAttribute('aria-expanded', 'true');

      const current = pop.querySelector('[aria-current="true"]');
      const list = current?.closest('.tc-loc__list');
      if (list && list.scrollHeight > list.clientHeight) {
        list.scrollTop = current.offsetTop - list.clientHeight / 2;
      }
      if (name === 'country' && this.search) {
        this.search.value = '';
        this.filter();
        // On a phone, focusing the field raises the keyboard over the list
        // someone is about to tap; only a pointer that hovers gets it.
        if (window.matchMedia('(hover: hover)').matches) this.search.focus({ preventScroll: true });
      }
    }

    close() {
      this.querySelectorAll('[data-pop]').forEach((p) => { p.hidden = true; });
      this.querySelectorAll('[data-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      this.open = null;
    }

    choose(field, value) {
      const input = this.field(field);
      if (!input) return;
      if (input.value === value) { this.close(); return; }
      input.value = value;
      this.classList.add('is-loading');
      this.form.submit();
    }

    async filter() {
      const typed = this.search.value.trim();
      const q = fold(typed);
      const groups = this.querySelector('[data-groups]');
      const results = this.querySelector('[data-results]');
      groups.hidden = !!q;
      results.hidden = !q;
      if (!q) return;

      if (!this.countries) {
        try {
          this.loading = this.loading || fetch(this.dataset.countriesUrl)
            .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
            .then(parseCountries);
          this.countries = await this.loading;
        } catch {
          this.loading = null;
          results.replaceChildren(this.note(this.dataset.error));
          return;
        }
        // A keystroke made while the list loaded draws its own results.
        if (fold(this.search.value.trim()) !== q) return;
      }

      const starts = this.countries.filter((c) => c.key.startsWith(q));
      const inside = this.countries.filter((c) => !c.key.startsWith(q) && c.key.includes(q));
      const hits = starts.concat(inside).slice(0, 50);
      results.replaceChildren(...(hits.length
        ? hits.map((c) => this.row(c))
        : [this.note((this.dataset.noResults || '').replace('__TERMS__', typed))]));
    }

    row(c) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'tc-loc__row';
      b.dataset.country = c.code;
      if (this.field('country_code')?.value === c.code) b.setAttribute('aria-current', 'true');

      const drawn = this.querySelector(`[data-groups] [data-country="${c.code}"] .tc-flag`);
      const flag = drawn ? drawn.cloneNode(true) : document.createElement('span');
      if (!drawn) {
        flag.className = 'tc-flag tc-flag--code';
        flag.setAttribute('aria-hidden', 'true');
        flag.textContent = c.code;
      }
      const name = document.createElement('span');
      name.className = 'tc-loc__row-name';
      name.textContent = c.name;
      b.append(flag, name);
      if (c.currency) {
        const cur = document.createElement('small');
        cur.textContent = c.currency;
        b.append(cur);
      }
      const tick = this.querySelector('.tc-loc__tick');
      if (tick) b.append(tick.cloneNode(true));
      return b;
    }

    note(text) {
      const div = document.createElement('div');
      div.className = 'tc-loc__note';
      div.textContent = text;
      return div;
    }

    onKey(e) {
      if (e.key === 'Escape' && this.open) {
        const trigger = this.trigger(this.open);
        this.close();
        trigger?.focus();
        return;
      }
      if (!this.open) return;
      const pop = this.pop(this.open);
      const rows = [...pop.querySelectorAll('.tc-loc__row')].filter((r) => r.offsetParent !== null);

      // Enter in the search field would submit the form as it stands.
      if (e.key === 'Enter' && e.target === this.search) {
        e.preventDefault();
        rows[0]?.click();
        return;
      }
      if ((e.key !== 'ArrowDown' && e.key !== 'ArrowUp') || !rows.length) return;
      e.preventDefault();
      const i = rows.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown'
        ? (i + 1) % rows.length
        : (i <= 0 ? rows.length - 1 : i - 1);
      rows[next].focus();
    }
  }

  customElements.define('tc-locale-bar', TcLocaleBar);
})();
